import { FC, ReactNode } from 'react';

import Image from 'molecules/Image';

interface NavItem {
  label: string;
  subtitle?: string;
  newBadge?: boolean;
  active?: boolean;
  icon: ReactNode;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const HomeIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ChainIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const FlowIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="15" y="15" width="6" height="6" rx="1" />
    <path d="M9 6h6a3 3 0 0 1 3 3v6" />
  </svg>
);

const TemplatesIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <rect x="3" y="3" width="18" height="6" rx="1" />
    <rect x="3" y="15" width="18" height="6" rx="1" />
  </svg>
);

const PeopleIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const PlayCircleIcon: FC<{ className?: string }> = ({ className = 'size-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className}>
    <circle cx="12" cy="12" r="10" />
    <polygon points="10 8 16 12 10 16 10 8" />
  </svg>
);

const StoreIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <path d="M3 9V21h18V9" />
    <path d="M3 9l2-6h14l2 6" />
    <path d="M3 9h18" />
  </svg>
);

const StopwatchIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <circle cx="12" cy="14" r="8" />
    <path d="M12 10v4l2 2" />
    <path d="M9 2h6" />
  </svg>
);

const MonitorIcon: FC<{ className?: string }> = ({ className = 'size-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className}>
    <rect x="3" y="4" width="18" height="6" rx="1" />
    <rect x="3" y="14" width="18" height="6" rx="1" />
  </svg>
);

const LogsIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="13" y2="16" />
  </svg>
);

const NAV_GROUPS: NavGroup[] = [
  { items: [{ label: 'Home', active: true, icon: <HomeIcon /> }] },
  {
    label: 'BUILD',
    items: [
      { label: 'Components', subtitle: 'Manage Connections', newBadge: true, icon: <ChainIcon /> },
      { label: 'Integrations', icon: <FlowIcon /> },
      { label: 'Workflow Templates', icon: <TemplatesIcon /> },
    ],
  },
  {
    label: 'DEPLOY',
    items: [
      { label: 'Customers', icon: <PeopleIcon /> },
      { label: 'Instances', icon: <PlayCircleIcon /> },
      { label: 'Marketplace', icon: <StoreIcon /> },
    ],
  },
  {
    label: 'MANAGE',
    items: [
      { label: 'Executions', icon: <StopwatchIcon /> },
      { label: 'Monitors', icon: <MonitorIcon /> },
      { label: 'Logs', icon: <LogsIcon /> },
    ],
  },
];

const Sidebar: FC = () => (
  <aside className="hidden h-full w-60 shrink-0 flex-col gap-5 bg-gray-900 px-3 py-4 text-white md:flex">
    <div className="flex items-center justify-between px-2">
      <Image
        url="/prismatic-logo.svg"
        alt="Prismatic logo"
        width={130}
        height={27}
        noFill
        className="h-auto w-[130px] object-contain"
      />
      <span className="font-roboto text-xs text-gray-400">{'<'}</span>
    </div>

    {NAV_GROUPS.map(group => (
      <div key={group.label || 'home'} className="flex flex-col gap-0.5">
        {group.label && (
          <span className="px-3 pb-2 pt-3 font-roboto text-xs font-semibold tracking-wider text-gray-400">
            {group.label}
          </span>
        )}
        {group.items.map(item => (
          <div
            key={item.label}
            className={`flex items-center gap-3 rounded-md px-3 py-2 font-roboto text-sm ${
              item.active ? 'bg-gray-800 text-white' : 'text-gray-100'
            }`}
          >
            <span className="text-gray-300">{item.icon}</span>
            <div className="flex flex-1 flex-col leading-tight">
              <span className="font-medium">{item.label}</span>
              {item.subtitle && (
                <span className="font-roboto text-xs text-gray-400">
                  {item.subtitle}
                  {item.newBadge && <span className="ml-1 text-success-400">• New</span>}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    ))}
  </aside>
);

const TopBar: FC = () => (
  <div className="flex h-14 shrink-0 items-center justify-end border-b border-gray-200 bg-white px-8">
    <div className="flex items-center gap-1.5 font-roboto text-sm text-gray-1000">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-4">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span>Help</span>
    </div>
  </div>
);

const WarningTriangleIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-5">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const StatCard: FC<{ value: string; label: string; iconColor: string; icon: ReactNode }> = ({
  value,
  label,
  iconColor,
  icon,
}) => (
  <div className="flex h-28 w-72 items-center gap-4 rounded-md border border-gray-200 bg-white px-6">
    <span className="font-roboto text-6xl font-regular leading-none text-success-400">{value}</span>
    <div className="flex flex-1 flex-col gap-1.5">
      <span className={iconColor}>{icon}</span>
      <span className="font-roboto text-sm font-semibold leading-tight text-gray-900">{label}</span>
    </div>
  </div>
);

const WelcomeRow: FC = () => (
  <div className="flex items-start justify-between gap-6">
    <div className="flex flex-col gap-2 pt-2">
      <p className="font-roboto text-3xl font-medium text-gray-900">Welcome</p>
      <p className="font-roboto text-sm text-gray-1000">Below you will find options to help you get started.</p>
    </div>
    <div className="flex gap-4">
      <StatCard value="0" label="Uncleared alert monitors" iconColor="text-gray-1000" icon={<WarningTriangleIcon />} />
      <StatCard
        value="3"
        label="Deployed instances"
        iconColor="text-gray-1000"
        icon={<PlayCircleIcon className="size-5" />}
      />
    </div>
  </div>
);

interface BuildCard {
  title: string;
  description: string;
  icon: ReactNode;
}

const RocketIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-gray-700">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09Z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2Z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

const TemplateGridIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-gray-700">
    <rect x="3" y="3" width="7" height="9" rx="1" />
    <rect x="14" y="3" width="7" height="5" rx="1" />
    <rect x="14" y="12" width="7" height="9" rx="1" />
    <rect x="3" y="16" width="7" height="5" rx="1" />
  </svg>
);

const ImportIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-gray-700">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const CodeBracketsIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-gray-700">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const BUILD_CARDS: BuildCard[] = [
  {
    title: 'Quickstart',
    description: 'Use the low-code designer to build your integration from the ground up.',
    icon: <RocketIcon />,
  },
  {
    title: 'Template',
    description: 'Choose a low-code template to use as a starting point in building your customized integration.',
    icon: <TemplateGridIcon />,
  },
  {
    title: 'Import',
    description: 'Import a low-code integration from a YAML definition.',
    icon: <ImportIcon />,
  },
  {
    title: 'Code-Native (CNI)',
    description: 'Build integrations in TypeScript using your preferred IDE.',
    icon: <CodeBracketsIcon />,
  },
];

const BuildIntegrationSection: FC = () => (
  <section className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-primary-50 p-5">
    <p className="font-roboto text-md font-semibold text-gray-900">Build an integration</p>
    <div className="flex gap-4">
      {BUILD_CARDS.map(card => (
        <div key={card.title} className="flex flex-1 flex-col gap-3 rounded-md border border-gray-200 bg-white p-4">
          {card.icon}
          <p className="font-roboto text-sm font-semibold text-gray-900">{card.title}</p>
          <p className="font-roboto text-xs leading-relaxed text-gray-1000">{card.description}</p>
        </div>
      ))}
    </div>
  </section>
);

const Y_AXIS_TICKS = ['3500', '2800', '2100', '1400', '700', '0'];
const X_AXIS_TICKS = ['9/15', '9/16', '9/17', '9/18', '9/19', '9/20', '9/21'];

const InstanceOperationsPanel: FC = () => (
  <div className="flex flex-1 flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6">
    <div className="flex items-center gap-2">
      <span className="text-gray-1000">
        <PlayCircleIcon className="size-5" />
      </span>
      <p className="font-roboto text-md font-semibold text-gray-900">Instance operations</p>
    </div>

    <div className="flex flex-1 gap-3">
      <ul className="flex flex-col justify-between py-1 font-roboto text-xs text-gray-500">
        {Y_AXIS_TICKS.map(tick => (
          <li key={tick}>{tick}</li>
        ))}
      </ul>
      <div className="flex-1">
        <svg viewBox="0 0 400 200" preserveAspectRatio="none" className="size-full">
          <defs>
            <linearGradient id="ftuxChartFillBlue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A6C6EB" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#A6C6EB" stopOpacity="0" />
            </linearGradient>
          </defs>
          <line x1="0" y1="0" x2="400" y2="0" stroke="#E4E6F1" strokeWidth="1" />
          <line x1="0" y1="40" x2="400" y2="40" stroke="#E4E6F1" strokeWidth="1" />
          <line x1="0" y1="80" x2="400" y2="80" stroke="#E4E6F1" strokeWidth="1" />
          <line x1="0" y1="120" x2="400" y2="120" stroke="#E4E6F1" strokeWidth="1" />
          <line x1="0" y1="160" x2="400" y2="160" stroke="#E4E6F1" strokeWidth="1" />
          <line x1="0" y1="200" x2="400" y2="200" stroke="#E4E6F1" strokeWidth="1" />
          <path
            d="M0,38 C40,30 80,30 120,40 S190,90 230,115 S320,90 400,95 L400,200 L0,200 Z"
            fill="url(#ftuxChartFillBlue)"
          />
          <path
            d="M0,38 C40,30 80,30 120,40 S190,90 230,115 S320,90 400,95"
            fill="none"
            stroke="#4573AE"
            strokeWidth="2"
          />
          <path d="M0,178 C60,176 120,178 180,176 S300,174 400,174" fill="none" stroke="#12B76A" strokeWidth="2" />
        </svg>
      </div>
    </div>

    <ul className="flex justify-between pl-9 font-roboto text-xs text-gray-500">
      {X_AXIS_TICKS.map(tick => (
        <li key={tick}>{tick}</li>
      ))}
    </ul>

    <div className="flex items-center justify-center gap-6 font-roboto text-xs text-gray-1000">
      <span className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-success-500" /> Failed
      </span>
      <span className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-primary-1500" /> Passed
      </span>
    </div>
  </div>
);

interface ActivityRow {
  initials: string;
  who: string;
  verb: string;
  target: string;
  time: string;
  avatarTone: string;
}

const ACTIVITY_ROWS: ActivityRow[] = [
  {
    initials: 'YO',
    who: 'You',
    verb: 'added a new Team Member',
    target: 'Test User',
    time: '2:20 PM',
    avatarTone: 'bg-primary-100 text-primary-1100',
  },
  {
    initials: 'YO',
    who: 'You',
    verb: 'added a new Customers',
    target: 'Test User',
    time: '1:59 PM',
    avatarTone: 'bg-primary-100 text-primary-1100',
  },
  {
    initials: 'JA',
    who: 'Jason',
    verb: 'deployed instance',
    target: 'Test Integration',
    time: '11:27 AM',
    avatarTone: 'bg-warning-100 text-warning-700',
  },
  {
    initials: 'TA',
    who: 'Tamara',
    verb: 'updated instance',
    target: 'Test Integration',
    time: '11:12 AM',
    avatarTone: 'bg-error-100 text-error-700',
  },
];

const Avatar: FC<{ initials: string; tone: string }> = ({ initials, tone }) => (
  <div
    className={`flex size-10 shrink-0 items-center justify-center rounded-full font-roboto text-xs font-semibold ${tone}`}
  >
    {initials}
  </div>
);

const ActivityFeedPanel: FC = () => (
  <div className="flex flex-1 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-gray-1000">
          <MonitorIcon className="size-5" />
        </span>
        <p className="font-roboto text-md font-semibold text-gray-900">Activity feed</p>
      </div>
      <div className="flex size-7 items-center justify-center rounded-md bg-gray-100 font-roboto text-md text-gray-1000">
        ⋯
      </div>
    </div>
    <div className="text-center font-roboto text-xs font-semibold tracking-wide text-gray-1000">MONDAY, MARCH 14</div>
    <div className="flex flex-col">
      {ACTIVITY_ROWS.map((row, index) => (
        <div
          key={`${row.who}-${row.time}`}
          className={`flex items-center gap-4 py-3 ${index > 0 ? 'border-t border-gray-200' : ''}`}
        >
          <Avatar initials={row.initials} tone={row.avatarTone} />
          <span className="flex-1 font-roboto text-sm text-gray-1000">
            <strong className="font-bold">{row.who}</strong> {row.verb}{' '}
            <span className="font-bold text-primary-1500">{row.target}</span>
          </span>
          <span className="font-roboto text-xs text-gray-1000">{row.time}</span>
        </div>
      ))}
    </div>
  </div>
);

const DashboardMockup: FC = () => (
  <div aria-hidden className="pointer-events-none flex size-full select-none bg-gray-50">
    <Sidebar />
    <div className="flex flex-1 flex-col">
      <TopBar />
      <div className="flex flex-1 flex-col gap-6 p-8">
        <WelcomeRow />
        <BuildIntegrationSection />
        <section className="flex flex-1 gap-4">
          <InstanceOperationsPanel />
          <ActivityFeedPanel />
        </section>
      </div>
    </div>
  </div>
);

export default DashboardMockup;
